import type { Node, NodeMouseHandler } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Background, Controls, MiniMap, Panel, ReactFlow, useNodesState } from '@xyflow/react';

import type { ContactWebFilters } from './filter-graph';
import type { ContactWebGraph, ContactWebGraphNode, RelationshipKind } from './types';
import { connectionsForNode, workingTogetherSince } from './connections';
import { ContactWebFilter } from './contact-web-filter';
import { ContactWebLegend } from './contact-web-legend';
import { ContactWebSearch } from './contact-web-search';
import { DetailPanel } from './detail-panel';
import { EMPTY_CONTACT_WEB_FILTERS, filterVisibleNodeIds } from './filter-graph';
import { HouseholdNodeCard } from './household-node';
import { layoutContactWebNodes } from './layout-graph';
import { ProfessionalNodeCard } from './professional-node';
import { RelationshipTypeToggle } from './relationship-type-toggle';

const NODE_TYPES = { household: HouseholdNodeCard, professional: ProfessionalNodeCard };

const DEFAULT_ACTIVE_KINDS: ReadonlySet<RelationshipKind> = new Set(['serves']);

const FALLBACK_NODE_WIDTH = 224;
const FALLBACK_NODE_HEIGHT = 88;
const FOCUS_ZOOM = 0.85;

type RenderNodeData = ContactWebGraphNode & { readonly faded: boolean; readonly focused: boolean };

function buildInitialNodes(graph: ContactWebGraph): Node<RenderNodeData>[] {
  const positions = layoutContactWebNodes(graph.nodes);
  return graph.nodes.map((node) => ({
    id: node.id,
    type: node.kind,
    position: positions.get(node.id) ?? { x: 0, y: 0 },
    data: { ...node, faded: false, focused: false },
  }));
}

type Props = {
  readonly graph: ContactWebGraph;
  readonly focusNodeId?: string | null;
  /** Hides the detail sheet on node click, e.g. when embedded in a modal that's already focused on one node. Selection/highlighting still works. */
  readonly showDetailPanel?: boolean;
};

export function ContactWebCanvas({ graph, focusNodeId = null, showDetailPanel = true }: Props) {
  const initialNodes = useMemo(() => buildInitialNodes(graph), [graph]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [activeKinds, setActiveKinds] = useState<ReadonlySet<RelationshipKind>>(DEFAULT_ACTIVE_KINDS);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(focusNodeId);
  const [filters, setFilters] = useState<ContactWebFilters>(EMPTY_CONTACT_WEB_FILTERS);

  useEffect(() => {
    setNodes(initialNodes);
    setSelectedNodeId(focusNodeId);
  }, [initialNodes, setNodes, focusNodeId]);

  const visibleEdges = useMemo(
    () => graph.edges.filter((edge) => activeKinds.has(edge.kind)),
    [graph.edges, activeKinds],
  );

  const connectedNodeIds = useMemo(() => {
    if (selectedNodeId === null) return null;
    const ids = new Set<string>([selectedNodeId]);
    for (const edge of visibleEdges) {
      if (edge.source === selectedNodeId) ids.add(edge.target);
      if (edge.target === selectedNodeId) ids.add(edge.source);
    }
    return ids;
  }, [selectedNodeId, visibleEdges]);

  const filterVisibleIds = useMemo(
    () => filterVisibleNodeIds(graph.nodes, visibleEdges, filters),
    [graph.nodes, visibleEdges, filters],
  );

  const renderNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          faded:
            (connectedNodeIds !== null && !connectedNodeIds.has(node.id)) ||
            (filterVisibleIds !== null && !filterVisibleIds.has(node.id)),
          focused: node.id === selectedNodeId,
        },
      })),
    [nodes, connectedNodeIds, filterVisibleIds, selectedNodeId],
  );

  const renderEdges = useMemo(
    () =>
      visibleEdges.map((edge) => {
        const highlighted = edge.source === selectedNodeId || edge.target === selectedNodeId;
        const filterFaded =
          filterVisibleIds !== null && !(filterVisibleIds.has(edge.source) && filterVisibleIds.has(edge.target));
        const faded = (connectedNodeIds !== null && !highlighted) || filterFaded;

        let opacity = 0.55;
        if (faded) {
          opacity = 0.12;
        } else if (highlighted) {
          opacity = 0.9;
        }

        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: 'straight',
          style: {
            stroke: highlighted ? 'var(--color-primary)' : undefined,
            strokeWidth: highlighted ? 2 : 1.5,
            strokeDasharray: edge.kind === 'refers' ? '5 4' : undefined,
            opacity,
          },
        };
      }),
    [visibleEdges, connectedNodeIds, filterVisibleIds, selectedNodeId],
  );

  // The detail sheet is modal, so the canvas goes inert while it's open — a node can only
  // be clicked again once selectedNodeId is already null, so there's no toggle-to-deselect
  // case to handle here.
  const handleNodeClick = useCallback<NodeMouseHandler>((_event, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const selectedNode = useMemo(
    () => (selectedNodeId === null ? null : (graph.nodes.find((node) => node.id === selectedNodeId) ?? null)),
    [selectedNodeId, graph],
  );
  const selectedConnections = useMemo(
    () => (selectedNodeId === null ? [] : connectionsForNode(selectedNodeId, graph)),
    [selectedNodeId, graph],
  );
  const selectedWorkingTogetherSince = useMemo(
    () => (selectedNodeId === null ? null : workingTogetherSince(selectedNodeId, graph)),
    [selectedNodeId, graph],
  );

  return (
    <div className="relative h-full min-h-[600px] w-full overflow-hidden">
      <ReactFlow
        nodes={renderNodes}
        edges={renderEdges}
        onNodesChange={onNodesChange}
        nodeTypes={NODE_TYPES}
        onNodeClick={handleNodeClick}
        onPaneClick={() => setSelectedNodeId(null)}
        onInit={(instance) => {
          if (focusNodeId === null) return;
          const node = instance.getNode(focusNodeId);
          if (!node) return;
          const width = node.measured?.width ?? FALLBACK_NODE_WIDTH;
          const height = node.measured?.height ?? FALLBACK_NODE_HEIGHT;
          void instance.setCenter(node.position.x + width / 2, node.position.y + height / 2, { zoom: FOCUS_ZOOM });
        }}
        fitView={focusNodeId === null}
        minZoom={0.1}
        maxZoom={1.5}
      >
        <Background />
        <Controls />
        <MiniMap pannable zoomable />
        <Panel position="top-left" className="flex items-center gap-3">
          <ContactWebSearch nodes={graph.nodes} onSelectNode={setSelectedNodeId} />
          <ContactWebFilter graph={graph} filters={filters} onChange={setFilters} />
          <RelationshipTypeToggle activeKinds={activeKinds} onChange={setActiveKinds} />
        </Panel>
        <Panel position="top-right">
          <ContactWebLegend />
        </Panel>
      </ReactFlow>
      {showDetailPanel && (
        <DetailPanel
          node={selectedNode}
          connections={selectedConnections}
          workingTogetherSince={selectedWorkingTogetherSince}
          onSelectNode={setSelectedNodeId}
          onClose={() => setSelectedNodeId(null)}
        />
      )}
    </div>
  );
}
